<template>
  <v-navigation-drawer
    :value="true"
    clipped
    bottom
    app
    :mini-variant="!isAccountSidebarOpen"
    width="325px"
    mobile-breakpoint="0"
    class="sidebar sidebar--account"
    :class="isMainSidebarOpen ? 'sidebar--account--translateX' : ''"
  >
    <v-list
      nav
      dense
      expand
    >
      <v-list-item class="d-flex justify-end pe-0">
        <v-btn
          id="btnAccountSidebar"
          icon
          @click="$store.dispatch('app/setSecondSidebarOpenState', !isAccountSidebarOpen)"
        >
          <v-icon>{{ toggleIcon }}</v-icon>
        </v-btn>
      </v-list-item>
      <AccountSidebarItem
        url="/v"
        icon="mdi-home"
        title="Dashboard"
        :isAccountSidebarOpen="isAccountSidebarOpen"
      />
      <v-divider class="my-3" />
      <div
        v-for="link in accountLinks"
        :key="link.title"
        class="mb-1"
      >
        <AccountSidebarGroup
          v-if="link.subLinks"
          :cssClass="link.cssClass"
          :icon="`fas fa-${link.icon}`"
          :title="link.title"
          :isAccountSidebarOpen="isAccountSidebarOpen"
          :isExternalLink="link.isExternal"
          :subLinks="link.subLinks"
          :isTreasurer="isTreasurer"
          :isTreasurerOrConsultant="isTreasurerOrConsultant"
          :isForTreasurerOrConsultant="link.isForTreasurerOrConsultant"
          :startSubmenuOpen="link.startSubmenuOpen"
          :storeLocation="link.storeLocation"
        />
        <AccountSidebarItem
          v-else
          :url="link.url"
          :cssClass="link.cssClass"
          :icon="`fas fa-${link.icon}`"
          :title="link.title"
          :isAccountSidebarOpen="isAccountSidebarOpen"
          :isExternalLink="link.isExternal"
          :isForTreasurersOnly="link.isForTreasurersOnly"
          :isTreasurer="isTreasurer"
          :isTreasurerOrConsultant="isTreasurerOrConsultant"
          :isForTreasurerOrConsultant="link.isForTreasurerOrConsultant"
        />
      </div>
    </v-list>
  </v-navigation-drawer>
</template>

<script>
import userPermissionLevel from "@/mixins/userPermissionLevel.js";
import { mapGetters } from 'vuex';
import AccountSidebarItem from "@/components/AccountSidebarItem.vue";
import AccountSidebarGroup from "@/components/AccountSidebarGroup.vue";

export default {
  mixins: [userPermissionLevel],
  components: {
    AccountSidebarItem,
    AccountSidebarGroup
  },
  data() {
    return {
      // isTreasurer: false,
    }
  },
  computed: {
    ...mapGetters({
      userId: "auth/getUserId",
      token: 'auth/getToken',
      isMainSidebarOpen: "app/getMainSidebarOpenState",
      isAccountSidebarOpen: "app/getSecondSidebarOpenState",
      isAccountSubmenuOpen: "app/getAccountSubmenuOpenState",
      isEventSubmenuOpen: "app/getEventSubmenuOpenState",
      isMerchandiseSubmenuOpen: "app/getMerchandiseSubmenuOpenState",
      isSuperAdmin: "auth/isSuperAdmin",
    }),
    toggleIcon() {
      return this.isAccountSidebarOpen ?
        'mdi-chevron-double-left' :
        'mdi-chevron-double-right';
    },
    accountId() {
      return this.$route.params.id;
    },
    authQuery() {
      return `user=${this.userId}&token=${this.token}`;
    },
    isTreasurer() {
      return this.$auth.isSuperAdminOrTreasurerForAccount(this.accountId);
    },
    isTreasurerOrConsultant() {
      if (this.isTreasurer) {
        return true;
      }

      return this.getUserPermissionLevel() <= 21;
    },
    showMerchandise() {
      if (this.isTreasurer) {
        return true; // covers super admin
      }

      return this.$store.getters["accounts/getAllowUsersMerchandise"](this.accountId) && this.$auth.isConsultantOrFundraiserForAccount(this.accountId);
    },
    accountLinks() {
      let accountLinks = [
        {
          title: 'Account Overview',
          url: `/v/account/${this.accountId}`,
          icon: 'tachometer-alt',
          isExternal: false,
          isForTreasurersOnly: false,
        },
        {
          title: 'Account Settings',
          icon: 'cog',
          isForTreasurersOnly: false,
          isForTreasurerOrConsultant: true,
          startSubmenuOpen: this.isAccountSubmenuOpen,
          storeLocation: "app/setAccountSubmenuOpenState",
          subLinks: [
            {
              title: 'Account Details',
              url: `/v/account/${this.accountId}/details`,
              isExternal: false,
              isForTreasurersOnly: false,
            },
            {
              title: 'Bank Information',
              url: `/v/account/${this.accountId}/banking`,
              isExternal: false,
              isForTreasurersOnly: true,
            },
            {
              title: 'Compliance',
              url: `/v/account/${this.accountId}/compliance`,
              isExternal: false,
              isForTreasurersOnly: false,
            },
            {
              title: 'Disbursements',
              url: `/v/account/${this.accountId}/disbursements`,
              isExternal: false,
              isForTreasurersOnly: false,
            },
            {
              title: 'Donation Page',
              url: `/v/account/${this.accountId}/donation-page`,
              isExternal: false,
              isForTreasurersOnly: false,
            },
            {
              title: 'Email Settings',
              url: `/v/account/${this.accountId}/EMailingList`,
              isExternal: false,
              isForTreasurerOrConsultant: true,
            },
            {
              title: 'Integrations',
              url: `/v/account/${this.accountId}/integrations`,
              isExternal: false,
              isForTreasurersOnly: false,
            },
          ],
        },
        {
          title: 'Bulk Import',
          url: `/v/account/${this.accountId}/imports/bulk`,
          icon: 'box',
          isExternal: false,
          isForTreasurersOnly: false,
        },
        {
          title: 'Campaigns',
          url: `/v/account/${this.accountId}/campaigns`,
          icon: 'bullhorn',
          isExternal: false,
          isForTreasurersOnly: false,
        },
        {
          title: 'Events',
          url: `/v/account/${this.accountId}/events`,
          icon: 'ticket-alt',
          isExternal: false,
          isForTreasurersOnly: false,
        },
        {
          title: 'Exports/Reports/Invoices',
          url: `/v/account/${this.accountId}/reports`,
          icon: 'file',
          isExternal: false,
          isForTreasurersOnly: false,
          isForTreasurerOrConsultant: true,
        },
        ...(this.showMerchandise ? [{
          title: 'Merchandise',
          icon: 'shopping-bag',
          isForTreasurersOnly: false,
          startSubmenuOpen: this.isMerchandiseSubmenuOpen,
          storeLocation: "app/setMerchandiseSubmenuOpenState",
          subLinks: [
            {
              title: 'Products',
              url: `/v/account/${this.accountId}/merchandise`,
              isExternal: false,
              isForTreasurersOnly: false,
            },
            {
              title: 'Settings',
              url: `/v/account/${this.accountId}/merchandise/settings`,
              isExternal: false,
              isForTreasurersOnly: false,
            },
          ],
        }] : []),
        {
          title: 'Page Design',
          url: `/v/account/${this.accountId}/page-design`,
          icon: 'desktop',
          isExternal: false,
          isForTreasurersOnly: false,
        },
        {
          title: 'Recurring Contributions',
          url: `/v/account/${this.accountId}/recurring`,
          icon: 'sync-alt',
          isExternal: false,
          isForTreasurersOnly: false,
          isForTreasurerOrConsultant: true,
        },
        {
          title: 'User Management',
          url: `/v/account/${this.accountId}/users`,
          icon: 'users',
          isExternal: false,
          isForTreasurersOnly: false,
        },
      ];

      if (this.isSuperAdmin || new Date() < new Date("2025-08-04")) {
        accountLinks.push(
          {
            title: 'View Old User Interface',
            url: `/f/admin/transactions.aspx?AID=${this.accountId}&${this.authQuery}`,
            icon: 'flag',
            isExternal: true,
            isForTreasurersOnly: false,
          });
      }

      accountLinks = accountLinks.map(account => {
        account.cssClass = this.isCurrentUrl(this.$route.path, account.url) ?
          'sidebar__item--active' :
          '';
        return account;
      });

      // return this.$auth.isSuperAdminOrTreasurerForAccount(this.$route.params.id) ? 
      //   accountLinks : 
      //   accountLinks.filter(account => !account.isForTreasurersOnly);

      return accountLinks;
    },
  },
  watch: {
    // secondSidebarOpenState(newValue) {
    //   this.isAccountSidebarOpen = newValue;
    // },
    // isAccountSidebarOpen(isOpen) {
    //   this.$store.dispatch("app/setSecondSidebarOpenState", isOpen);
    // },
  },
  methods: {
    isCurrentUrl(currentPath, linkPath) {
      return currentPath === linkPath;
    },
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .v-list-item--link:hover {
    text-decoration: none;
  }
  .sidebar--account__icon {
    width: 1.4em;
    flex-shrink: 0;
  }
  .theme--light.v-list-item--active:before,
  .theme--light.v-list-item--active:hover:before {
    opacity: 0;
  }
  .theme--light.v-list-item--active.sidebar__item--active:before,
  .theme--light.v-list-item--active.sidebar__item--active:hover:before {
    opacity: 0.12;
    background-color: rgb(76, 178, 241);
  }
  .v-list .v-list-item--active.sidebar__item--active,
  .v-list .v-list-item--active.sidebar__item--active .v-icon {
    // color: #4cb2f1;
    color: #0093ee !important;
  }
}

@media all and (min-width: 769px) {
  .sidebar--account {
    transform: translateX(0) !important;
  }
  .sidebar--account--translateX {
    transform: translateX(325px) !important;
  }
}
</style>
